import { TrashIcon, WarningDiamondIcon } from "@xola/icons";
import { Button, Popover, Select } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import clsx from "clsx";
import { uniqueId, update } from "lodash";
import React, { ChangeEvent, useState } from "react";
import { VariantQuantity } from "../updateQuantityHelper";
import {
    RETURN_ACTION_OPTIONS,
    RETURN_REASONS,
    ReturnAction,
    ReturnAdjustmentRow,
    ReturnAdjustmentsByTemplateId,
} from "./returnVariantHelper";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { QuantitySpinner } from "@/components/Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ManageActionButton } from "@/components/Dashboard/Roster/ManageActionButton";
import { ACTION_LABELS, AdjustAction } from "@/components/Product/MerchandiseInventory/adjustInventoryActions";
import { useVisibility } from "@/hooks/useVisibility";

interface ReturnVariantAdjustmentProps {
    templateId: string;
    variant: VariantQuantity;
    maxAdjustmentQty: number;
    delta: number;
    unfulfilledReduction: number;
    rows: ReturnAdjustmentRow[];
    allocatedQty: number;
    totalAllocatedQty: number;
    errorMessages: string[];
    updateRowsByTemplateId: (
        updater: (previous: ReturnAdjustmentsByTemplateId) => ReturnAdjustmentsByTemplateId,
    ) => void;
}

export const ReturnVariantAdjustment = ({
    templateId,
    variant,
    maxAdjustmentQty,
    delta,
    unfulfilledReduction,
    rows,
    allocatedQty,
    totalAllocatedQty,
    errorMessages,
    updateRowsByTemplateId,
}: ReturnVariantAdjustmentProps) => {
    const updateTemplateRows = useMemoizedFn(
        (updater: (templateRows: ReturnAdjustmentRow[]) => ReturnAdjustmentRow[]) => {
            updateRowsByTemplateId((previous) => {
                return update({ ...previous }, [templateId], (templateRows: ReturnAdjustmentRow[] = []) =>
                    updater(templateRows),
                ) as ReturnAdjustmentsByTemplateId;
            });
        },
    );

    const handleAddRow = useMemoizedFn((row: ReturnAdjustmentRow) => {
        updateTemplateRows((templateRows) => [...templateRows, row]);
    });

    const handleUpdateRow = useMemoizedFn((rowId: string, patch: Partial<Omit<ReturnAdjustmentRow, "id">>) => {
        updateTemplateRows((templateRows) =>
            templateRows.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
        );
    });

    const handleDeleteRow = useMemoizedFn((rowId: string) => {
        updateTemplateRows((templateRows) => templateRows.filter((row) => row.id !== rowId));
    });

    return (
        <div
            className="rounded border border-gray-lighter"
            data-testid={`return-inventory-table-for-${variant.variantName}`}
        >
            <div className="flex items-center justify-between bg-gray-lighter px-4 py-2">
                <NoTranslate className="text-sm font-bold">{variant.variantName}</NoTranslate>
                <div
                    className="flex items-center space-x-3 text-sm text-gray-dark"
                    data-testid="return-inventory-header"
                >
                    {errorMessages.length > 0 && (
                        <span className="flex items-center">
                            <WarningDiamondIcon className="text-danger" />
                        </span>
                    )}
                    <span className={clsx(totalAllocatedQty < delta ? "text-danger" : "text-gray-dark")}>
                        <var data-var="totalAllocatedQty">{totalAllocatedQty}</var>/<var data-var="delta">{delta}</var>
                    </span>
                </div>
            </div>

            <div className="space-y-2 p-4 text-sm text-gray-dark" data-testid="return-inventory-row">
                {unfulfilledReduction > 0 && (
                    <div className="flex items-center space-x-2">
                        <span className="flex-1">Move to available</span>
                        <span className="font-bold">
                            <var data-var="unfulfilledReduction">{unfulfilledReduction}</var>
                        </span>
                    </div>
                )}

                {/* each row represents an adjustment that has been made for reduction in the fulfilled quantity */}
                {maxAdjustmentQty > 0 &&
                    rows.map((row) => {
                        const maxForRow = maxAdjustmentQty - (allocatedQty - row.qty);

                        return (
                            <div
                                key={row.id}
                                className="flex items-center space-x-2"
                                data-testid="return-inventory-adjustment-row"
                            >
                                <span
                                    className={clsx("flex-1", {
                                        "text-danger": errorMessages.length > 0,
                                    })}
                                >
                                    {row.reason}
                                </span>
                                <QuantitySpinner
                                    value={row.qty}
                                    min={1}
                                    max={maxForRow}
                                    size="medium"
                                    minTooltipText="The minimum number of items needed in order to book is"
                                    shouldSyncValue={false}
                                    onChange={(qty) => handleUpdateRow(row.id, { qty })}
                                />
                                <SecondaryButton
                                    size="tiny"
                                    data-testid="delete-return-adjustment-row"
                                    onClick={() => handleDeleteRow(row.id)}
                                >
                                    <TrashIcon />
                                </SecondaryButton>
                            </div>
                        );
                    })}

                {errorMessages.length > 0 && (
                    <div className="space-y-2 text-danger">
                        {errorMessages.map((message) => (
                            <div key={message} className="flex items-start">
                                <WarningDiamondIcon className="mt-0.5 flex-shrink-0 text-danger" />
                                <span className="ml-2">
                                    <var data-var="returnErrorMessage">{message}</var>
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {maxAdjustmentQty > 0 && (
                    <ReturnInventoryAddAdjustmentPopover
                        disabled={allocatedQty >= maxAdjustmentQty}
                        onApply={handleAddRow}
                    />
                )}
            </div>
        </div>
    );
};

interface ReturnInventoryAddAdjustmentPopoverProps {
    disabled: boolean;
    onApply: (row: ReturnAdjustmentRow) => void;
}

const ReturnInventoryAddAdjustmentPopover = ({ disabled, onApply }: ReturnInventoryAddAdjustmentPopoverProps) => {
    const [visible, { show, hide }] = useVisibility(false);
    const defaultAction = AdjustAction.MOVE_TO_AVAILABLE;
    const [action, setAction] = useState<ReturnAction>(defaultAction);
    const [reason, setReason] = useState(RETURN_REASONS[defaultAction][0]);

    const handleActionChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const nextAction = event.target.value as ReturnAction;
        setAction(nextAction);
        setReason(RETURN_REASONS[nextAction][0]);
    };

    const handleApply = () => {
        onApply({
            id: uniqueId("return-row-"),
            action,
            reason,
            qty: 1,
        });
        hide();
    };

    return (
        <span className="inline-block">
            <Popover visible={visible} placement="top" appendTo={document.body} onClickOutside={hide}>
                <ManageActionButton text="Add Adjustment" disabled={disabled} onClick={show} />
                <Popover.Content>
                    <div className="w-72 space-y-3 rounded-lg bg-white p-4">
                        <div className="space-y-1">
                            <label className="block text-xs font-semibold">Action</label>
                            <Select value={action} className="w-full" onChange={handleActionChange}>
                                {RETURN_ACTION_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {ACTION_LABELS[value]}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <label className="block text-xs font-semibold">Reason</label>
                            <Select
                                value={reason}
                                className="w-full"
                                onChange={(event: ChangeEvent<HTMLSelectElement>) => setReason(event.target.value)}
                            >
                                {RETURN_REASONS[action].map((itemReason, index) => (
                                    <option key={itemReason} value={itemReason}>
                                        {index === 0 ? `${itemReason} (default)` : itemReason}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="flex justify-end space-x-2">
                            <SecondaryButton onClick={hide}>Cancel</SecondaryButton>
                            <Button color="primary" onClick={handleApply}>
                                Apply
                            </Button>
                        </div>
                    </div>
                </Popover.Content>
            </Popover>
        </span>
    );
};
